@file:Suppress("ktlint:standard:max-line-length")

package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.royceecoleman.bill.billing.importNoteFile
import com.royceecoleman.bill.billing.newBillingRequestId
import com.royceecoleman.bill.shared.billing.BillingClient
import com.royceecoleman.bill.shared.billing.BillingProfile
import com.royceecoleman.bill.shared.billing.InvoiceDetails
import com.royceecoleman.bill.shared.billing.NoteImportRequest
import com.royceecoleman.bill.shared.billing.NoteParsing
import com.royceecoleman.bill.shared.billing.SourceNote
import com.royceecoleman.bill.shared.billing.WorkspaceCreate
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.js.Promise

/** Applies a saved client's (and optionally one matter's) defaults to a new invoice. */
internal fun applyClient(
    invoice: InvoiceDetails,
    client: BillingClient,
    matterId: String,
): InvoiceDetails {
    val keeper = client.timekeepers.firstOrNull()
    val matter = client.matters.firstOrNull { it.id == matterId }
    return invoice.copy(
        clientId = client.clientId,
        firmId = client.firmId,
        currency = client.currency,
        timekeeperId = keeper?.id ?: invoice.timekeeperId,
        timekeeperName = keeper?.name ?: invoice.timekeeperName,
        timekeeperClassification = keeper?.classification ?: invoice.timekeeperClassification,
        hourlyRate = matter?.hourlyRate?.ifBlank { null } ?: keeper?.hourlyRate ?: invoice.hourlyRate,
        additionalTimekeepers = client.timekeepers.drop(1),
        matterId = matter?.matterId ?: invoice.matterId,
        clientMatterId = matter?.clientMatterId ?: invoice.clientMatterId,
        clientHasNoMatterId = matter?.clientHasNoMatterId ?: invoice.clientHasNoMatterId,
    )
}

internal suspend fun readFileBase64(file: File): String {
    val dataUrl =
        Promise<String> { resolve, reject ->
            val reader = FileReader()
            reader.onload = { resolve(reader.result.unsafeCast<String>()) }
            reader.onerror = { reject(IllegalStateException("The file could not be read.")) }
            reader.readAsDataURL(file)
        }.await()
    return dataUrl.substringAfter(",")
}

@Composable
internal fun NewWorkspaceForm(
    profiles: List<BillingProfile>,
    clients: List<BillingClient>,
    busy: Boolean,
    dirty: () -> Unit,
    selectedProfile: String = "",
    initialInvoice: InvoiceDetails = InvoiceDetails(),
    initialClientId: String = "",
    initialMatterId: String = "",
    create: (WorkspaceCreate) -> Unit,
) {
    var title by remember { mutableStateOf("") }
    var profileId by remember { mutableStateOf(profiles.firstOrNull { it.id == selectedProfile }?.id ?: profiles.firstOrNull()?.id.orEmpty()) }
    var clientId by remember { mutableStateOf(initialClientId.takeIf { id -> clients.any { it.id == id } }.orEmpty()) }
    var matterId by remember { mutableStateOf(initialMatterId) }
    var invoice by remember { mutableStateOf(initialInvoice) }
    var notesText by remember { mutableStateOf("") }
    var preview by remember { mutableStateOf<List<SourceNote>?>(null) }
    var warnings by remember { mutableStateOf<List<String>>(emptyList()) }
    var error by remember { mutableStateOf("") }
    val requestId = remember { newBillingRequestId() }
    val scope = rememberCoroutineScope()
    Eyebrow("03 / CAPTURE THE MONTH")
    H1 { Text("Bring the rough notes.") }
    GuideSteps(3)
    P({ classes("bill-muted") }) {
        Text(
            "One workspace covers one matter and calendar month. Paste one task per line — BILL reads dates and times written like \"3/4 1.2 call w/ OC\" or \"(0.3)\" — or import a CSV, Excel, TSV or text export from your timekeeping software. Check the preview before saving.",
        )
    }
    if (profiles.isEmpty()) {
        P({ classes("bill-alert") }) { Text("Create a billing profile under Examples & client rules first.") }
        return
    }
    val client = clients.firstOrNull { it.id == clientId }
    Div({ classes("bill-card") }) {
        BillField("Workspace title", title, "September · Acme litigation") {
            title = it
            dirty()
        }
        if (clients.isNotEmpty()) {
            Div({ classes("bill-two") }) {
                BillSelect("Client (optional)", clientId, mapOf("" to "— Enter details manually —") + clients.associate { it.id to it.name }) { id ->
                    clientId = id
                    matterId = ""
                    clients.firstOrNull { it.id == id }?.let { chosen ->
                        invoice = applyClient(invoice, chosen, "")
                        chosen.defaultProfileId.takeIf { p -> profiles.any { it.id == p } }?.let { profileId = it }
                    }
                    dirty()
                }
                if (client != null) {
                    BillSelect("Matter", matterId, mapOf("" to "— Choose a matter —") + client.matters.associate { it.id to it.name }) { id ->
                        matterId = id
                        invoice = applyClient(invoice, client, id)
                        client.matters.firstOrNull { it.id == id }?.profileId?.takeIf { p -> profiles.any { it.id == p } }?.let { profileId = it }
                        dirty()
                    }
                }
            }
        }
        BillSelect("Billing profile", profileId, profiles.associate { it.id to it.name }) {
            profileId = it
            dirty()
        }
        BillField("Billing month (YYYY-MM)", invoice.month, "2026-09") {
            invoice = invoice.copy(month = it)
            dirty()
        }
        BillDetails("Matter details — optional until review") {
            InvoiceEditor(invoice, { invoice }, showMonth = false) {
                invoice = it
                dirty()
            }
        }
    }
    Div({ classes("bill-card") }) {
        BillArea(
            "Original work notes",
            notesText,
            "One note per line, e.g.\n9/2 0.5 rev opposing MSJ re causation\ncall client re status (0.2)",
            "notes",
        ) {
            notesText = it
            preview = null
            dirty()
        }
        BillField("All sources are from the same day (optional, YYYY-MM-DD)", invoice.workDate, "Leave blank if the dates differ") {
            invoice = invoice.copy(workDate = it.trim())
            dirty()
        }
        Div({ classes("bill-row") }) {
            BillButton("Preview notes", busy || notesText.isBlank()) {
                val result = NoteParsing.parseText(notesText, invoice.month)
                preview = result.notes
                warnings = result.warnings
                error = ""
            }
        }
        Label(attrs = { classes("bill-field") }) {
            Text("Or import a timekeeping export (.csv, .xlsx, .tsv or .txt, maximum 256 KB)")
            Input(InputType.File, attrs = {
                attr("accept", ".csv,.xlsx,.tsv,.txt,text/csv,text/plain,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
                if (busy) attr("disabled", "")
                onChange { event ->
                    val file = event.target.files?.item(0)
                    if (file != null) {
                        scope.launch {
                            try {
                                require(file.size.toDouble() <= 262144) { "Choose a file smaller than 256 KB." }
                                val result = importNoteFile(NoteImportRequest(file.name, readFileBase64(file), invoice.month))
                                preview = result.notes
                                warnings = result.warnings
                                error = ""
                                dirty()
                            } catch (cancel: CancellationException) {
                                throw cancel
                            } catch (e: Exception) {
                                error = e.message.orEmpty()
                            }
                        }
                    }
                }
            })
        }
        P({ classes("bill-muted") }) {
            Text("Time must come from your records. BILL reads only times you wrote down and never estimates or rounds them. Photos and PDFs are not supported; transcribe or export them first.")
        }
        if (warnings.isNotEmpty()) {
            Ul({ classes("bill-issues") }) { warnings.forEach { Li { Text("Check: $it") } } }
        }
        preview?.let { rows -> NotesPreview(rows, invoice.workDate, { preview.orEmpty() }) { preview = it } }
        if (error.isNotBlank()) {
            P({
                classes("bill-alert")
                attr("role", "alert")
            }) { Text(error) }
        }
        BillButton("Save notes & open review →", busy, "primary") {
            try {
                val notes = preview ?: parseNotesStrict(notesText, invoice.month)
                require(notes.isNotEmpty()) { "Add at least one rough note." }
                require(notes.size <= NoteParsing.MAX_NOTES) { "A workspace holds at most ${NoteParsing.MAX_NOTES} notes. Split the month into several workspaces." }
                require(notes.all { it.text.isNotBlank() }) { "Every note needs text. Remove empty rows from the preview." }
                require(invoice.workDate.isEmpty() || validWorkDate(invoice.workDate, invoice.month)) {
                    "The shared date must be a YYYY-MM-DD date within the billing month, or blank."
                }
                create(WorkspaceCreate(requestId, title, profileId, invoice, notes, clientId, matterId))
                error = ""
            } catch (e: Exception) {
                error = e.message.orEmpty()
            }
        }
    }
}

/** A valid YYYY-MM-DD date inside the billing month (YYYY-MM). */
internal fun validWorkDate(
    date: String,
    month: String,
): Boolean = month.length == 7 && date.startsWith("$month-") && NoteParsing.parseDate(date) == date

private fun parseNotesStrict(
    text: String,
    month: String,
) = com.royceecoleman.bill.billing.parseBillingNotes(text, month)

@Composable
private fun NotesPreview(
    rows: List<SourceNote>,
    sharedDate: String,
    latest: () -> List<SourceNote>,
    change: (List<SourceNote>) -> Unit,
) {
    fun update(
        index: Int,
        note: SourceNote,
    ) = change(latest().mapIndexed { i, it -> if (i == index) note else it })
    Div({ classes("bill-card") }) {
        H2 { Text("Preview: ${rows.size} notes") }
        P({ classes("bill-muted") }) {
            Text(
                "Correct anything BILL misread. Date and minutes are optional: leave them blank if they are not in your records. A blank date uses the shared day above, if set. You can add them during review; LEDES export needs both.",
            )
        }
        rows.forEachIndexed { index, note ->
            key(index) {
                Div({ classes("bill-grid") }) {
                    BillField("Note ${index + 1}", note.text) { update(index, latest()[index].copy(text = it)) }
                    BillField("Date (YYYY-MM-DD)", note.date, sharedDate) { update(index, latest()[index].copy(date = it.trim())) }
                    BillField("Minutes", note.minutes?.toString().orEmpty()) { update(index, latest()[index].copy(minutes = it.trim().toIntOrNull())) }
                    BillField("Timekeeper ID (optional)", note.timekeeperId) { update(index, latest()[index].copy(timekeeperId = it.trim())) }
                    BillButton("Remove", LocalWorkbenchBusy.current, "danger") { change(latest().filterIndexed { i, _ -> i != index }) }
                }
            }
        }
    }
}
